import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Star, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROFILE_LABELS = { parent: 'Parent', provider: 'Childcare Provider', worker: 'Care Professional', camp: 'Camp / Program', admin: 'Team Member' };
const label = (t) => PROFILE_LABELS[(t || '').toLowerCase()] || 'Member';

export function ReviewModal({ open, onOpenChange, onSubmitted }) {
  const { user, getAuthHeaders } = useAuth();
  const [features, setFeatures] = useState([]);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [f, mine] = await Promise.all([
          axios.get(`${API}/app-reviews/features`),
          axios.get(`${API}/app-reviews/mine`, { headers: getAuthHeaders() }),
        ]);
        if (cancel) return;
        setFeatures(f.data.features || []);
        if (mine.data) {
          setEditing(true);
          setRating(mine.data.rating || 0);
          setText(mine.data.review_text || '');
          setSelected(mine.data.features || []);
        } else {
          setEditing(false); setRating(0); setText(''); setSelected([]);
        }
      } catch (e) { /* noop */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [open]);const toggleFeature = (f) => {
    setSelected(prev => prev.includes(f) ? prev.filter(x => x !== f) : (prev.length >= 4 ? prev : [...prev, f]));
  };

  const submit = async () => {
    if (rating < 1) return toast.error('Please pick a star rating.');
    if (!text.trim()) return toast.error('Please write a short review.');
    setSaving(true);
    try {
      await axios.post(`${API}/app-reviews`, { rating, review_text: text.trim(), features: selected }, { headers: getAuthHeaders() });
      toast.success(editing ? 'Your review was updated — thanks!' : "Thanks for your review! You're entered in the draw. 🎉");
      onOpenChange(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit your review.');
    } finally { setSaving(false); }
  };

  const firstName = (user?.name || '').trim() || 'You';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-panel border-white/40 rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="app-review-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka' }}>{editing ? 'Edit your review' : 'Leave us a review'}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 font-medium" data-testid="review-name">{firstName}</span>
              <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-600" data-testid="review-profile-type">{label(user?.user_type)}</span>
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Your rating</p>
              <div className="flex gap-1" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" data-testid={`review-star-${n}`}
                    onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)} className="p-0.5">
                    <Star className={`w-8 h-8 transition-colors ${(hover || rating) >= n ? 'text-amber-400' : 'text-slate-300'}`} fill={(hover || rating) >= n ? '#fbbf24' : 'none'} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Your review</p>
              <Textarea data-testid="review-text" value={text} onChange={e => setText(e.target.value)} maxLength={1500}
                placeholder="Tell others what you love about ChildCareConnections..." className="glass-input rounded-xl min-h-[110px]" />
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">What do you think are our best features? <span className="text-slate-400 font-normal">(pick up to 4)</span></p>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {features.map((f, i) => {
                  const on = selected.includes(f);
                  const disabled = !on && selected.length >= 4;
                  return (
                    <button key={f} type="button" data-testid={`review-feature-${i}`} onClick={() => toggleFeature(f)} disabled={disabled}
                      className={`text-left text-sm rounded-xl px-3 py-2 border transition-colors flex items-center gap-2 ${on ? 'border-violet-400 bg-violet-50 text-violet-700' : disabled ? 'border-slate-100 text-slate-500 cursor-not-allowed' : 'border-slate-200 text-slate-100 hover:bg-white/10'}`}>
                      <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${on ? 'bg-violet-500' : 'border border-slate-300'}`}>{on && <Check className="w-3 h-3 text-white" />}</span>
                      <span className="min-w-0">{f}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button data-testid="review-submit" onClick={submit} disabled={saving} className="w-full rounded-xl bg-violet-500 text-white hover:bg-violet-600 h-11">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{editing ? 'Update review' : 'Submit review'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ReviewModal;
